import { AxiosError } from 'axios';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { useTheme } from 'styled-components';

import AddBtn from '../../../../../../components/Buttons/AddBtn';
import Modal from '../../../../../../components/Modal';
import AsyncTable, { Column, RequestPage } from '../../../../../../components/AsyncTable';
import {
  backendErrorNotification,
  frontendNotification,
} from '../../../../../../components/Notification';
import SelectAsyncJSX from '../../../../../../components/SelectAsync';
import BasicDateTimePicker from '../../../../../../components/SelectDateTime';
import SingleSelect from '../../../../../../components/SingleSelect';
import HttpStatus from '../../../../../../enums/httpStatus';
import { DeleteIcon, EditIcon } from '../../../../../../icons';
import {
  customersApi,
  sellersApi,
  visitRoutineAPI,
} from '../../../../../../services/requests';
import { ApiResPaginated } from '../../../../../../types/apiResponseTypes';
import { SellerVisitRoutineSummary, SellerVisitRoutine } from '../../../../../../types';
import { DateFrequency } from '../../../../../../utils/enums';
import {
  getOptionsFromCustomers,
  getOptionsFromSellers,
} from '../../../../../../utils/getSelectOptions';
import {
  currentDateWithTimeString,
  frequencyIndex,
  getFrequencyOptions,
  getVisitRoutineWeekDayOptions,
  visitRoutineWeekDays,
} from '../../../../../../utils/helpers';
import ModalDelete from '../ModalDelete';
import ModalEdit from '../ModalEditRoutine';

const parseDateTimeValue = (value?: string | null) => {
  if (!value) {
    return null;
  }

  return dayjs(value.replace(' ', 'T'));
};

type Props = {
  closeModal: () => void;
  selectedItem: SellerVisitRoutineSummary | null;
  callback: () => void;
};

type Option = {
  value: any;
  label: string;
};

const getSelectValue = (option?: Option | Record<string, unknown> | null) => {
  if (!option || typeof option !== 'object' || !('value' in option)) {
    return undefined;
  }

  return option.value;
};

const ModalManageRoutines = ({ closeModal, selectedItem, callback }: Props) => {
  const theme = useTheme();
  const [waitingRequest, setWaitingRequest] = useState(false);
  const [seller, setSeller] = useState<Option | Record<string, unknown> | null>(null);
  const [customer, setCustomer] = useState<Option | Record<string, unknown> | null>(null);
  const [sellerVisits, setSellerVisits] = useState<SellerVisitRoutine[] | []>([]);
  const [routineTableVersion, setRoutineTableVersion] = useState(0);
  const [selectedVisit, setSelectedVisit] = useState<SellerVisitRoutine | null>(null);
  const [showModalDelete, setShowModalDelete] = useState(false);
  const [showModalEdit, setShowModalEdit] = useState(false);
  const [frequency, setFrequency] = useState<Option | Record<string, unknown>>({
    label: t('weekly'),
    value: DateFrequency.weekly,
  });
  const [weekDay, setWeekDay] = useState<Option | Record<string, unknown>>({
    label: t('monday'),
    value: 'segunda',
  });
  const [startDate, setStartDate] = useState<any>(parseDateTimeValue(currentDateWithTimeString()));
  const [salesTargetValue] = useState('0.00');
  const [note] = useState('-');

  const sellerValue = getSelectValue(seller);
  const customerValue = getSelectValue(customer);
  const isDailyFrequency = Number(frequency.value) === DateFrequency.daily;
  const minimumAllowedStartDate = dayjs().startOf('minute');
  const parsedStartDate = startDate ? dayjs(startDate) : null;
  const hasValidStartDate = !!parsedStartDate
    && parsedStartDate.isValid()
    && !parsedStartDate.isBefore(minimumAllowedStartDate);

  const validateCreateRoutine = () => {
    if (!sellerValue) {
      frontendNotification({
        message: t('selectSeller'),
        type: 'warning',
      });

      return false;
    }

    if (!customerValue) {
      frontendNotification({
        message: t('selectCustomer'),
        type: 'warning',
      });

      return false;
    }

    if (!hasValidStartDate) {
      frontendNotification({
        message: t('visitRoutineStartDateMustBeCurrentMinuteOrFuture'),
        type: 'warning',
      });

      return false;
    }

    return true;
  };

  const refreshRoutineTable = () => {
    setRoutineTableVersion((currentValue) => currentValue + 1);
  };

  const getSellerVisitsPage: RequestPage = async ({ search, page, quantityPerPage }) => {
    const sellerCode = sellerValue ? String(sellerValue) : '';
    if (!sellerCode) {
      return {
        data: [],
        hasMore: false,
        totalPage: 0,
      };
    }

    try {
      const queryParams = {
        CodigoVendedor: sellerCode,
        page,
        quantityPerPage,
        search,
      };

      const res = await visitRoutineAPI.get(queryParams);

      if (res.status !== HttpStatus.OK) {
        throw res;
      }

      const { data, paginated }: ApiResPaginated<SellerVisitRoutine[]> = res.data;

      return {
        data,
        hasMore: page < paginated.totalPage,
        totalPage: paginated.totalPage,
      };
    } catch (err) {
      if (!visitRoutineAPI.axios.isCancel(err)) {
        backendErrorNotification(err as AxiosError<any, any>);
      }

      return {
        data: [],
        hasMore: false,
        totalPage: 0,
      };
    }
  };

  const reqCreate = async () => {
    if (!validateCreateRoutine()) {
      return;
    }

    setWaitingRequest(true);

    try {
      const data = {
        CodigoVendedor: sellerValue,
        CodigoCliente: customerValue,
        Periodicidade: frequency.value.toString(),
        DiaSemana: String(weekDay.value),
        start_date: dayjs(startDate).format('YYYY-MM-DD HH:mm:ss'),
        VlrMetaVenda: salesTargetValue,
        Obs: note,
      };

      const res = await visitRoutineAPI.store(data);

      if (res.status === HttpStatus.OK) {
        refreshRoutineTable();
        setCustomer(null);

        frontendNotification({
          message: t('successfullyCreated'),
          type: 'success',
        });
      } else {
        throw res;
      }
    } catch (err) {
      if (!visitRoutineAPI.axios.isCancel(err)) {
        backendErrorNotification(err as AxiosError<any, any>);
      }
    }

    setWaitingRequest(false);
  };

  const closeModalAction = () => {
    callback();
    closeModal();
  };

  useEffect(() => {
    setCustomer(null);
  }, [sellerValue]);

  useEffect(() => {
    if (selectedItem) {
      setSeller({
        value: selectedItem.CodigoVendedor,
        label: `${selectedItem.CodigoVendedor} - ${selectedItem.Nome}`,
      });
    }
  }, []);

  const getRoutineWeekDayLabel = (visit: SellerVisitRoutine) => {
    if (visit.Periodicidade === DateFrequency.daily) {
      return '-';
    }

    return visitRoutineWeekDays()[visit.DiaSemana] ?? visit.DiaSemana;
  };

  const routineColumns: Column<SellerVisitRoutine>[] = [
    {
      label: t('customer') as string,
      Cell: (visit) => (
        <div style={{ fontWeight: 600 }}>
          {`${visit.CodigoCliente} - ${visit.customer?.RazaoSocial ?? '-'}`}
        </div>
      ),
      cellStyle: tableCellStyle(theme.colors.onBackground),
      headerCellStyle: tableHeaderStyle,
    },
    {
      label: t('frequency') as string,
      Cell: (visit) => frequencyIndex()[visit.Periodicidade],
      cellStyle: tableCellStyle(theme.colors.onBackground),
      headerCellStyle: tableHeaderStyle,
    },
    {
      label: t('weekDay') as string,
      Cell: getRoutineWeekDayLabel,
      cellStyle: tableCellStyle(theme.colors.onBackground),
      headerCellStyle: tableHeaderStyle,
    },
    {
      label: t('routineStartDate') as string,
      accessor: 'start_date',
      cellStyle: tableCellStyle(theme.colors.onBackground),
      headerCellStyle: tableHeaderStyle,
    },
    {
      label: t('note') as string,
      Cell: (visit) => (
        <div
          title={visit.Obs ?? '-'}
          style={{
            maxWidth: '14rem',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {visit.Obs ?? '-'}
        </div>
      ),
      cellStyle: tableCellStyle(theme.colors.onBackground),
      headerCellStyle: tableHeaderStyle,
    },
    {
      label: t('actions') as string,
      Cell: (visit) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <EditIcon
            title={t('edit') as string}
            style={{ cursor: 'pointer' }}
            onClick={() => {
              setSelectedVisit(visit);
              setShowModalEdit(true);
            }}
          />
          <DeleteIcon
            title={t('delete') as string}
            style={{ cursor: 'pointer' }}
            onClick={() => {
              setSelectedVisit(visit);
              setShowModalDelete(true);
            }}
          />
        </div>
      ),
      cellStyle: tableCellStyle(theme.colors.onBackground),
      headerCellStyle: tableHeaderStyle,
    },
  ];

  return (
    <>
      <Modal
        title={t('visitRoutines')}
        onlyCloseButton
        showModal
        closeModal={closeModalAction}
        actionNameBtn={t('save')}
        isLoading={waitingRequest}
        style={{ width: 'min(70rem, 96vw)', maxWidth: '96vw' }}
        bodyStyle={{ width: '100%' }}
      >
        <div>
          <div className="row">
            <div className="itemFormWithLabel">
              <div className="label">{t('selectSeller') as string}</div>
              <SelectAsyncJSX
                value={seller}
                onChange={(e: any) => setSeller(e)}
                request={sellersApi}
                reqResponseToOption={getOptionsFromSellers}
                disabled={waitingRequest}
                isRequired
              />
            </div>

            <div className="itemFormWithLabel">
              <div className="label">{t('sellersCustomers') as string}</div>
              <SelectAsyncJSX
                value={customer}
                onChange={(e: any) => setCustomer(e)}
                request={customersApi}
                reqResponseToOption={getOptionsFromCustomers}
                query={sellerValue ? `sellerCode=${sellerValue}` : ''}
                cacheUniqs={[sellerValue]}
                disabled={waitingRequest || !sellerValue}
                isRequired
              />
            </div>
          </div>

          <div className="row">
            <div className="itemFormWithLabel">
              <div className="label">{t('frequency') as string}</div>
              <SingleSelect
                value={frequency}
                onChange={(e: any) => setFrequency(e)}
                options={getFrequencyOptions({ includeAlways: false })}
                disabled={waitingRequest}
              />
            </div>

            <div className="itemFormWithLabel">
              <div className="label">{t('weekDay') as string}</div>
              <SingleSelect
                value={weekDay}
                onChange={(e: any) => setWeekDay(e)}
                options={getVisitRoutineWeekDayOptions()}
                disabled={waitingRequest || isDailyFrequency}
              />
            </div>

            <div className="itemFormWithLabel">
              <div className="label">{t('routineStartDate') as string}</div>
              <BasicDateTimePicker
                value={startDate}
                onChange={setStartDate}
                disabled={waitingRequest}
                disablePast={false}
                minDateTime={minimumAllowedStartDate}
                label=""
              />
            </div>

            <div className="inputWithoutLabel">
              <div className="label">&nbsp;</div>
              <AddBtn onClick={() => reqCreate()} disabled={waitingRequest} />
            </div>
          </div>

          {sellerValue ? (
            <div
              style={{
                border: `1px solid ${theme.colors.onBackground}20`,
                borderRadius: '0.75rem',
                overflow: 'hidden',
                margin: '1rem',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '1rem',
                  padding: '0.9rem 1rem',
                  background: theme.colors.surface,
                  borderBottom: `1px solid ${theme.colors.onBackground}10`,
                }}
              >
                <strong>{t('visitRoutines') as string}</strong>
              </div>

              <AsyncTable
                tableName={t('visitRoutines') as string}
                columns={routineColumns}
                value={sellerVisits}
                onChange={setSellerVisits}
                requestPage={getSellerVisitsPage}
                reqListener={[routineTableVersion, sellerValue]}
                defaultQuantityPerPage={10}
                options={{
                  hideDownloads: true,
                  quantityPerPageLabel: t('quantityPerPage'),
                  totalPageLabel: `${t('totalPages')}: `,
                  searchPlaceholder: t('search'),
                  styles: {
                    table: { minWidth: '52rem' },
                    primaryColor: theme.colors.surface,
                    secondaryColor: theme.colors.onSurface,
                    alternateRowColor: theme.colors.onSurfaceLight,
                    textColor: theme.colors.text,
                    downloadBtns: { marginLeft: '0.5rem' },
                  },
                }}
              />
            </div>
          ) : (
            <h3 style={{ opacity: 0.5, textAlign: 'center', margin: '3rem' }}>
              {t('selectSeller') as string}
            </h3>
          )}
        </div>
      </Modal>

      {showModalEdit && selectedVisit && (
        <ModalEdit
          closeModal={() => {
            setShowModalEdit(false);
            setSelectedVisit(null);
          }}
          selecteditem={selectedVisit}
          reqCallback={refreshRoutineTable}
        />
      )}

      {showModalDelete && selectedVisit && (
        <ModalDelete
          closeModal={() => {
            setShowModalDelete(false);
            setSelectedVisit(null);
          }}
          selecteditem={selectedVisit}
          reqCallback={refreshRoutineTable}
        />
      )}
    </>
  );
};

export default ModalManageRoutines;

const tableHeaderStyle = {
  padding: '0.85rem 1rem',
  textAlign: 'left' as const,
  verticalAlign: 'middle' as const,
  fontSize: '0.85rem',
  fontWeight: 700,
  whiteSpace: 'nowrap' as const,
  borderBottom: 'inherit',
};

const tableCellStyle = (borderColor: string) => ({
  padding: '0.85rem 1rem',
  textAlign: 'left' as const,
  verticalAlign: 'middle' as const,
  borderBottom: `1px solid ${borderColor}10`,
});
